import usePrivateAxios from '@/common/api/usePrivateAxios'
import { getRailsUpdate } from '@/common/utils/getRailsUpdate'
import { mapToDeletion } from '@/common/utils/railsHelpers'
import { unFormatCurrency, unFormatPercentage } from '@/common/utils/unFormatters'
import { UpdateContractFeeDetailsSchema } from '@/features/establishments/schemas/contract/updateFeeDetails.schema'
import { useMutation } from '@tanstack/react-query'

interface UseUpdateContractFeeDetails {
  contractId?: string
  currentContractFees?: Array<{ id: string; fee_template: { id: string } }>
  onSuccess?: (response: any) => void
  onError?: (error: any) => void
}

export const useUpdateContractFeeDetails = ({
  contractId,
  currentContractFees,
  onSuccess,
  onError,
}: UseUpdateContractFeeDetails) => {
  const privateAxios = usePrivateAxios()

  return useMutation({
    mutationKey: ['updateContractFeeDetails'],
    mutationFn: async (data: UpdateContractFeeDetailsSchema) => {
      const railsUpdateResult = getRailsUpdate(
        currentContractFees || [],
        data.contract.contract_fees_attributes,
        (currentFee) => currentFee.id,
        (updatedFee) => updatedFee.fee_id || '',
        mapToDeletion,
        (newFee) => ({
          fee_template_id: newFee.fee_template_id,
          value:
            newFee.calculate_as === 'percentage' ? unFormatPercentage(newFee.value) : unFormatCurrency(newFee.value),
          main_fee: newFee.main_fee || false,
        }),
      )

      const updatedExistingFees = data.contract.contract_fees_attributes
        ?.filter((fee) => !!fee.fee_id)
        ?.map((fee) => ({
          id: fee.fee_id,
          main_fee: fee.main_fee || false,
        }))

      const contract = {
        contract_fees_attributes: [...railsUpdateResult, ...updatedExistingFees],
      }

      await privateAxios.put(`/establishment/contracts/${contractId}/update_contract_fees_information`, {
        contract,
      })
    },
    onSuccess,
    onError,
  })
}
